import { useTranslation } from 'react-i18next'

import { Box, Typography } from '@mui/material'

import { TLGAccordion } from '@components/TLGAccordion'

import {
  accordionSx,
  containerSx,
  contentGridSx,
  percentItemsTextSx,
} from './styles'

const ContentSection = () => {
  const { t } = useTranslation()

  return (
    <Box data-testid="tech-vendors-faq-accordion-section" sx={containerSx}>
      <Box sx={contentGridSx}>
        <TLGAccordion
          accordionSx={accordionSx}
          title={t('How many Tech Customers are on the Platform today?')}
        >
          <Typography variant="body1">
            {t(
              "While the portal was launched in February of this year, we've been facilitating matches between IT End users and Resellers/Partners for the past 4 years. Over this period, we've successfully connected more than 20,000 end users with solution providers tailored to address their specific pain points."
            )}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t('How is participation managed during the MVP Phase?')}
        >
          <Typography variant="body1">
            {t(
              "During our MVP Phase, we've limited participation to 250 technology providers for every 10,000 end users. This strategic approach ensures a balanced blend of supply and demand. Every client is assigned a dedicated Customer Success Manager to ensure consistent visibility and matching. Additionally, we've restricted the number of technology subscribers aligned with each Solution Focus, offering ample but not overwhelming options for IT End-users. Once the target subscriber count per Solution Focus is reached, we'll initiate a waiting list for others to join. This list will be fueled by new Tech Customers joining and any spaces opening due to active technology subscribers canceling their subscriptions."
            )}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t(
            "What's the current company size distribution of End Users on the platform?"
          )}
        >
          <Typography variant="body1">
            {t(
              "Our marketplace caters to Tech Customers of all sizes, encompassing SMB and Enterprise accounts spanning various commercial sectors. Over the past 4 years, we've worked with SMB and Enterprise accounts ranging from $10 million to over $50 billion in size. Presently, our Tech Customer universe is segmented as follows:"
            )}
          </Typography>
          <Typography sx={percentItemsTextSx}>
            {'- ' +
              t('60% of companies range from $25 million to $250 million')}
          </Typography>
          <Typography sx={percentItemsTextSx}>
            {'- ' + t('30% of companies range from $250 million to $1 billion')}
          </Typography>
          <Typography sx={percentItemsTextSx}>
            {'- ' + t('10% of companies exceed $1 billion')}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t('How is the growth plan promoted to Tech Customers?')}
        >
          <Typography variant="body1">
            {t(
              'Leveraging our repository of more than 8 million IT contacts, we utilize our two decades of marketing expertise to introduce them to our Matchmaking Portal. Our approach involves Referral Programs, Email Marketing, Social Media Campaigns, and active participation in prominent IT events across the USA, such as Gartner IT Xpo and Enterprise Connect. This multi-faceted strategy is geared towards having a minimum of 100,000 Tech Customers onboard and actively using our Platform by the close of 2024.'
            )}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t(
            'Will there be an overlap in Tech Customer connections for competing solutions?'
          )}
        >
          <Typography variant="body1">
            {t(
              'Based on extensive focus groups conducted over the last 6 months, our Tech Customer community expressed a preference for efficiently matching with the best solution provider rather than dealing with numerous vendors. Anticipating this, we expect most Tech Customers to connect with a maximum of 2-3 competing solution providers simultaneously.'
            )}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t('What types of searches are most common?')}
        >
          <Typography variant="body1">
            {t(
              'The three most common searches are related to cybersecurity, cloud, and managed services. However, Tech Customers can search for any technology solutions and services based on their unique needs and requirements. We recommend creating solution landing pages aligned to your core offerings. The more landing pages you create, the more likely you are to be found in Tech Customer searches.'
            )}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t('What type of vendors are signed up?')}
        >
          <Typography variant="body1">
            {t(
              'We have a diverse set of technology partners featured in the marketplace from small VARs all the way up to large distributors. Because technologymatch.com is a destination site that caters to the needs of Tech Customers of all sizes, it’s important that we have a diverse array of technology clients and solutions featured in the platform.'
            )}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t('How many connections can we expect?')}
        >
          <Typography variant="body1">
            {t(
              'The number of connections is highly dependent upon the type of technology solutions and services you offer and the demand for those solutions and services. It’s also highly correlated with the number of solution pages you publish. The more solutions you publish, the more likely a prospective buyer is to connect because each solution page covers more surface area. Your onboarding Success Coach can guide you on the best way to proceed with setup.'
            )}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t('Do you guarantee results (connections/sales)?')}
        >
          <Typography variant="body1">
            {t(
              'We do not guarantee results. Our marketplace offers Tech Customers the ability to research, vet, and connect with potential technology partners and solutions. The Tech Customer journey is shifting to digital, self-serve channels. We can guarantee that a prospective buyer in buying mode will not be able to find or connect with you if you are not listed in the marketplace.'
            )}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t('How does the site work?')}
        >
          <Typography variant="body1">
            {t(
              'The site is very simple: Tech Customers enter search terms based on their technology needs. The marketplace feeds the Tech Customers Solution Landing Page results that are a precise fit for their search terms. Tech Customers review the information about you and your solution on the landing page, then they can decline or accept the match. When they accept the match, they will show up in your Vendor App Matches file and you can reach out to them. Tech Customers can also chat or schedule a call with you directly in the marketplace app.'
            )}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t('Do the End Users pay?')}
        >
          <Typography variant="body1">
            {t('Our marketplace is free for end users.')}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t('Is the site global or North American focused?')}
        >
          <Typography variant="body1">
            {t(
              'Today, the site is focused in North America, with plans to take it global in 2025.'
            )}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t(
            'What are the benefits of using technologymatch.com for Tech Customers?'
          )}
        >
          <Typography variant="body1">
            {t(
              'technologymatch.com provides Tech Customers with a streamlined and efficient way to find technology solutions and services that precisely match their needs. By using our platform, Tech Customers can access a diverse array of technology providers, review detailed Solution Landing Pages, and connect with potential partners. The benefits include saving time and effort in the search for the right technology solutions, accessing transparent pricing information, reading authentic customer reviews, and having the power to initiate connections with technology vendors.'
            )}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t(
            'How do technology vendors benefit from joining technologymatch.com?'
          )}
        >
          <Typography variant="body1">
            {t(
              'Technology vendors can leverage technologymatch.com to reach a highly targeted and actively engaged audience of Tech Customers. By creating Solution Landing Pages and participating in the marketplace, technology vendors can increase their visibility, build trust with potential customers, and receive connection requests from Tech Customers who are genuinely interested in their solutions. This platform offers a cost-effective way to generate high-quality leads and connect with Tech Customers who are actively seeking technology solutions.'
            )}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t(
            'How do I create a Solution Landing Page for my technology offerings?'
          )}
        >
          <Typography variant="body1">
            {t(
              'Creating a Solution Landing Page is a straightforward process. Our platform provides easy-to-use tools and templates for technology vendors to showcase their solutions. You can include detailed information about your products or services, pricing, customer testimonials, and other relevant content. Your dedicated Success Coach can guide you through the steps to create an effective landing page that resonates with Tech Customers.'
            )}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t(
            'Are there any fees for technology vendors to participate in technologymatch.com?'
          )}
        >
          <Typography variant="body1">
            {t(
              'Yes, there is a flat rate subscription fee for technology vendors to access and participate in the platform. The subscription fee covers the cost of maintaining your presence on the platform, receiving connection requests from Tech Customers, and accessing valuable analytics and insights. '
            )}
          </Typography>
        </TLGAccordion>

        <TLGAccordion
          accordionSx={accordionSx}
          title={t('Can I have a free trial?')}
        >
          <Typography variant="body1">
            {t(
              "We do not offer free trials for our platform. Instead, we provide a white glove setup of the solution to ensure you have a seamless and effective experience right from the start. Our dedicated Success Coaches will work closely with you to create Solution Landing Pages, optimize your presence, and guide you through the platform's features and tools. This personalized onboarding process is designed to maximize your success and help you achieve your goals on the platform. If you have any questions or need further assistance, our support team and Customer Success Managers are here to help you every step of the way."
            )}
          </Typography>
        </TLGAccordion>
      </Box>
    </Box>
  )
}

export { ContentSection }
